import {h, Fragment} from "preact"
import { SerpElement } from "@nosto/preact"
import { useState, useEffect, useMemo } from "preact/hooks";
import { productImagePlaceholder } from "../helpers";

export default function Product({ product, gridOption, showOnModel, productIndex, context }) {

    const safeProduct = {
        productId: product.productId || "unknown",
        name: product.name || "Unknown Product",
        url: product.url || "#",
        imageUrl: product.imageUrl || productImagePlaceholder,
        alternateImageUrls: product.alternateImageUrls || [],
        price: product.price || 0,
        listPrice: product.listPrice || null,
        skus: product.skus || [],
        customFields: product.customFields || [],
        brand: product.brand || null,
        pid: product.pid || null,
    };
  
    const handleize = value => {
        return value
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/-+/g, "-")
            .replace(/^-|-$/g, "");
    };

    // Extract all option types and values dynamically from customFields
    const optionTypes = [];
    safeProduct.skus?.forEach(sku => {
        sku.customFields?.forEach(field => {
            if (field.key !== "size" && field.key !== "color" && field.key !== "colour") return;

            let optionType = optionTypes.find(opt => opt.key === field.key);
            if (!optionType) {
                optionType = {
                    key: field.key,
                    name: field.key.charAt(0).toUpperCase() + field.key.slice(1),
                    values: new Set(),
                };
                optionTypes.push(optionType);
            }

            const handleizedValue = handleize(field.value);
            optionType.values.add(handleizedValue);
        });
    });

    optionTypes.forEach(opt => {
        opt.values = [...opt.values];
    });

    // Initialize selected options based on the first SKU
    const initialOptions = {};

    if (safeProduct.skus?.[0]?.customFields) {
        safeProduct.skus[0].customFields.forEach(field => {
            if (field.key !== "size" && field.key !== "color" && field.key !== "colour") return;
            initialOptions[field.key] = handleize(field.value);
        });
    }

    const [selectedOptions, setSelectedOptions] = useState(initialOptions || {});

    // Update selectedVariant whenever selectedOptions changes
    const [selectedVariant, setSelectedVariant] = useState(safeProduct.skus?.[0] || {});

    useEffect(() => {
        const matchingVariant = safeProduct.skus?.find(sku => {
            const variantOptions = {};
            sku.customFields?.forEach(field => {
                if (field.key !== "size" && field.key !== "color" && field.key !== "colour") return;
                variantOptions[field.key] = handleize(field.value);
            });

            return optionTypes.every(optType => {
                const optionKey = optType.key;
                return variantOptions[optionKey] === selectedOptions[optionKey];
            });
        });

        if (matchingVariant) {
            setSelectedVariant(matchingVariant);
        }
    }, [selectedOptions, safeProduct.skus]);


    // Handle option changes
    const handleOptionChange = (optionKey, value) => {

        setSelectedOptions(prev => ({
            ...prev,
            [optionKey]: value,
        }));
    };



    //Quantity Change
    const quantityClick = (event) => {
        const container = event.target.closest('quantity-input');
        const input = container.querySelector('.quantity__input');
        
        const previousValue = Number(input.value)
        if(previousValue === 0 && event.currentTarget.name === 'minus') {
           return;
        }

        event.currentTarget.name === 'plus' ? input.value = previousValue + 1 : input.value = previousValue - 1
    }

    // Access plpSettings from window
    const settings = window.plpSettings || {};

    // Extract product type from customFields or default to empty string
    const productType = safeProduct.customFields?.find(field => field.key === "product_type")?.value || "";

    // Price manipulation logic
    let productPrice = safeProduct.price;
    let displayCompareAtPrice = null;
    let savingsPercentage = null;

    // Check for sitewide discount
    if (
        settings.show_sitewide_discount &&
        settings.sitewide_discount_percentage &&
        productType !== "Gift Cards"
    ) {
        const customDiscount = 100 - settings.sitewide_discount_percentage;
        productPrice = (safeProduct.price * customDiscount) / 100;
        displayCompareAtPrice = safeProduct.price;
    }
    // Check for metafield discount
    else if (
        settings.show_metafield_discount &&
        safeProduct.customFields?.find(field => field.key === "custom-discount_percentage")?.value &&
        productType !== "Gift Cards"
    ) {
        const metafieldDiscount = parseFloat(
            safeProduct.customFields.find(field => field.key === "custom-discount_percentage").value
        );
        const customDiscount = 100 - metafieldDiscount;
        productPrice = (safeProduct.price * customDiscount) / 100;
        displayCompareAtPrice = safeProduct.price;
    }

    // Check for compare-at price (listPrice)
    if (safeProduct.listPrice && safeProduct.listPrice > productPrice) {
        displayCompareAtPrice = safeProduct.listPrice;
    }

    // Calculate savings percentage
    if (displayCompareAtPrice && displayCompareAtPrice > productPrice) {
        savingsPercentage = Math.round(
            ((displayCompareAtPrice - productPrice) / displayCompareAtPrice) * 100
        );
    }

    // Check for sale status
    const isOnSale = !!displayCompareAtPrice && displayCompareAtPrice > productPrice;

    const colorFilter = document.querySelectorAll(".ns-color-swatches .ns-swatch-option input[type='checkbox']");
    const selectedFilter = document.querySelectorAll(".ns-sidebar-submenu .ns-color-swatches .ns-swatch-option");

    // Extract images for carousel
    let images = [safeProduct.imageUrl, ...(safeProduct.alternateImageUrls || [])].filter(Boolean);
    let originalImages = [safeProduct.imageUrl, ...(safeProduct.alternateImageUrls || [])].filter(Boolean);

    let matchingVariantImg = null;

    if(document.querySelector('#collectionTemplate')) {
        const filterColor = document.querySelector('#collectionTemplate').getAttribute('data-filter-selected');
        
        if(filterColor != '') {
            matchingVariantImg = safeProduct.skus?.filter(sku => sku.name.toLowerCase().replace(/\s+/g, '-').includes(filterColor))[0]?.imageUrl;
            
            if(matchingVariantImg) {
                images = [matchingVariantImg, ...images.filter(img => img !== matchingVariantImg)];
            }
        }
    }

    const url = window.location.href;

    // Set selected variant and images back to default if no active color filters
    if(!url.includes('customFields.colour')) {
        if(document.querySelector('#collectionTemplate')) {
            document.querySelector('#collectionTemplate').setAttribute('data-filter-selected', '');
        }
        images = [originalImages[0], ...images.filter(img => img !== matchingVariantImg)];
    }

    if (showOnModel) {
        let color = selectedOptions.colour || selectedOptions.color;
        if (color) {
            // Find the index of the first image containing both "model" and the color
            const matchIndex = images.findIndex(image =>
                image.toLowerCase().includes("model") &&
                image.toLowerCase().includes(color)
            );

            // If a match is found, move it to the beginning
            if (matchIndex !== -1) {
                const [matchingImage] = images.splice(matchIndex, 1); // Remove the matching image
                images.unshift(matchingImage); // Add it to the beginning
            }
        }
    }

    // Extract rating from customFields
    const ratingData = safeProduct.customFields?.find(field => field.key === "reviews-rating");
    const rating = ratingData ? JSON.parse(ratingData.value).value : null;
    const ratingCount = safeProduct.customFields?.find(field => field.key === "reviews-rating_count")?.value || 0;
    const ratingScaleMax = ratingData ? JSON.parse(ratingData.value).scale_max || 5 : 5;

    // Badge for sale or brand messaging
    const badge = isOnSale
    ? {
        label: settings.translations?.on_sale || settings.translations?.on_sale.includes("Sale") || settings.translations?.on_sale.includes("SALE") || "Sale",
        style: {
            backgroundColor: settings.badge_sale_bg,
            color: settings.badge_sale_text,
            borderColor: settings.badge_sale_border,
            borderRadius: `${settings.badge_corner_radius}px`,
        },
    }
    : safeProduct.customFields?.find(field => field.key === "custom-brand_messaging")?.value
    ? {
        label: safeProduct.customFields?.find(field => field.key === "custom-brand_messaging").value,
        style: {
            backgroundColor: settings.badge_brand_bg,
            color: settings.badge_brand_text,
            borderColor: settings.badge_brand_border,
            borderRadius: `${settings.badge_corner_radius}px`,
        },
    }
    : safeProduct.customFields?.find(field => field.key === "custom-custom_badge_text")?.value
    ? {
        label: safeProduct.customFields?.find(field => field.key === "custom-custom_badge_text").value,
        style: {
            backgroundColor: safeProduct.customFields?.find(field => field.key === "custom-custom_badge_background_color").value,
            color: safeProduct.customFields?.find(field => field.key === "custom-custom_badge_text_color").value,
            borderColor: safeProduct.customFields?.find(field => field.key === "custom-custom_badge_border_color").value,
            borderRadius: `${settings.badge_corner_radius}px`,
        },
    }
    : null;

    // Format price with currency settings
    const formatPrice = price => {
        const formatted = Math.ceil(price * Shopify.currency.rate).toFixed(0);
        let result = formatted;
        if (settings.show_currency_icon && settings.currency_symbol) {
            result = `${settings.currency_symbol}${result}`;
        }
        if (settings.show_currency_code && settings.currency_code) {
            result = `${result} ${settings.currency_code}`;
        }
        return result;
    };

    return gridOption === "3" ? (
        <SerpElement
            as="div"
            className="product-card relative w-full h-full overflow-hidden"
            hit={product}
            >
            <a href={safeProduct.url} className="block w-full h-full">
                <div
                    className="relative w-full"
                    style={{ aspectRatio: "120/143" }}
                >
                    <img
                        className="w-full h-full object-cover"
                        src={showOnModel ? images[0] : selectedVariant.imageUrl || images[0] || productImagePlaceholder}
                        alt={safeProduct.name}
                        width="480"
                        height="574"
                        loading="lazy"
                    />
                </div>
            </a>
        </SerpElement>
    ) : (
        <SerpElement
            as="product-card"
            hit={product}
            className="product-card product-card-nosto relative flex flex-col h-full w-full color-bg-2"
            id={`product-card-${safeProduct.productId}`}
            data-product-id={safeProduct.productId}
        >
            <div class="overflow-hidden">
              {/* Media Container */}
            <div
                className="product-card__media-container relative w-full overflow-hidden"
                style={{ aspectRatio: "120/143" }}
            >

                {/* Primary Image */}
                <div className="product-card__media absolute inset-0 w-full h-full transition-opacity duration-300">
                    <a href={safeProduct.url} className="block w-full h-full">
                        <img
                            className="w-full h-full object-cover"
                            src={showOnModel ? images[0] : (matchingVariantImg || selectedVariant.imageUrl) || images[0] || productImagePlaceholder}
                            alt={safeProduct.name}
                            width="480"
                            height="574"
                            loading="lazy"
                        />
                    </a>
                </div>

                {/* Secondary Image (Hover) */}
                {settings.show_image_carousel === false && images.length > 1 && (
                    <div className="product-card__media-secondary absolute inset-0 w-full h-full opacity-0 transition-opacity duration-300">
                        <a href={safeProduct.url} className="block w-full h-full">
                            <img
                                className="w-full h-full object-cover"
                                src={images[1]}
                                alt={`${safeProduct.name} - Secondary`}
                                width="480"
                                height="574"
                                loading="lazy"
                            />
                        </a>
                    </div>
                )}

                {/* Carousel */}
                {settings.show_image_carousel && images.length > 1 && (
                    <>
                        {/* Desktop Carousel */}
                        <div
                            className="product-card__carousel-container absolute inset-0 w-full h-full opacity-0 transition-opacity duration-300 sm:block"
                            data-autoplay={settings.carousel_autoplay}
                            data-carousel-enabled="true"
                        >
                            <slider-component className="relative w-full h-full min-h-[100px] images-carousel">
                                <ul
                                    className="swiper-wrapper w-full h-full min-h-[100px] min-w-[100px]"
                                    role="list"
                                >
                                    {images.map((image, index, color) => {
                                        const matchingVariant = safeProduct.skus?.find(sku => sku.imageUrl === image);
                                        const selectedColorHandle = selectedVariant?.customFields?.find(field => field.key === "colour")?.value
                                        ? handleize(selectedVariant.customFields.find(field => field.key === "colour").value)
                                        : handleize(safeProduct.name);

                                        if(image.includes(selectedColorHandle)) {
                                            return (
                                                <li
                                                    key={image}
                                                    className="swiper-slide w-full h-full relative"
                                                >
                                                    <a href={safeProduct.url} className="block w-full h-full">
                                                        <img
                                                            className="w-full h-full object-cover"
                                                            src={image}
                                                            alt={`${safeProduct.name} - Image ${index + 1}`}
                                                            width="480"
                                                            height="574"
                                                            loading="lazy"
                                                            name={`${safeProduct.name}`}
                                                            data-color={selectedColorHandle}
                                                        />
                                                    </a>
                                                </li>
                                            );
                                        }
                                    })}
                                </ul>
                                <div className="slider-buttons no-js-hidden flex justify-between absolute inset-y-[77.5%] transform -translate-y-[77.5%] w-full min-w-[100px] px-2 z-[99]">
                                    <button
                                        type="button"
                                        className="slider-button slider-button--prev w-[32px] h-[32px] flex-shrink-0 flex items-center justify-center text-black"
                                        aria-label={"Previous"}
                                    >
                                        <svg
                                            aria-hidden="true"
                                            focusable="false"
                                            class="w-[24px] h-[24px]"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M15 19l-7-7 7-7"></path>
                                        </svg>
                                    </button>
                                    <button
                                        type="button"
                                        className="slider-button slider-button--next w-[32px] h-[32px] flex-shrink-0 flex items-center justify-center text-black"
                                        aria-label={"Next"}
                                    >
                                        <svg
                                            aria-hidden="true"
                                            focusable="false"
                                            class="w-[24px] h-[24px]"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 5l7 7-7 7"></path>
                                        </svg>
                                    </button>
                                </div>
                            </slider-component>
                        </div>
                        {/* Mobile Carousel */}
                        <div
                            className="product-card__mobile-carousel absolute inset-0 w-full h-full lg:hidden"
                            data-autoplay={settings.carousel_autoplay}
                        >
                            <slider-component class="relative w-full h-full min-h-[100px] swiper-container" data-mobile-swiper>
                                <ul
                                    className="swiper-wrapper w-full h-full min-h-[100px] min-w-[100px]"
                                    role="list"
                                >
                                    {images.map((image, index, color) => {
                                        const matchingVariant = safeProduct.skus?.find(sku => sku.imageUrl === image);
                                        const selectedColorHandle = selectedVariant?.customFields?.find(field => field.key === "colour")?.value
                                            ? handleize(selectedVariant.customFields.find(field => field.key === "colour").value)
                                            : handleize(safeProduct.name);
                            
                                        if(image.includes(selectedColorHandle)) {
                                            return (
                                                <li
                                                    key={image}
                                                    className="swiper-slide w-full h-full relative"
                                                >
                                                    <a href={safeProduct.url} className="block w-full h-full">
                                                        <img
                                                            className="w-full h-full object-cover"
                                                            src={image}
                                                            alt={`${safeProduct.name} - Image ${index + 1}`}
                                                            width="480"
                                                            height="574"
                                                            loading="lazy"
                                                            data-color={selectedColorHandle}
                                                        />
                                                    </a>
                                                </li>
                                            );
                                        }
                                    })}
                                </ul>
                                
                                {context === 'search' && (
                                   <div class="slider-buttons no-js-hidden flex justify-between absolute inset-y-[80%] transform -translate-y-[80%] w-full min-w-[100px] px-4 z-20">
                                        <button
                                        type="button"
                                        class="slider-button slider-button--prev-mob w-[32px] h-[32px] flex-shrink-0 flex items-center justify-center text-black"
                                        name="previous"
                                        aria-label="previous"
                                        >
                                        <svg
                                            aria-hidden="true"
                                            focusable="false"
                                            class="w-[24px] h-[24px]"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M15 19l-7-7 7-7"></path>
                                        </svg>
                                        </button>
                                        <button
                                        type="button"
                                        class="slider-button slider-button--next-mob w-[32px] h-[32px] flex-shrink-0 flex items-center justify-center text-black"
                                        name="next"
                                        aria-label="next"
                                        >
                                        <svg
                                            aria-hidden="true"
                                            focusable="false"
                                            class="w-[24px] h-[24px]"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 5l7 7-7 7"></path>
                                        </svg>
                                        </button>
                                    </div>
                                )}
                            </slider-component>
                        </div>
                    </>
                )}

                {/* Badge */}
                {badge && (
                    <div
                        className={`product-card__badge absolute left-[8px] z-10 flex justify-center items-center py-1 px-3 gap-2 border border-solid text-xs font-gerstner uppercase tracking-[0.24px] leading-[150%] ${context === 'search' ? 'top-[7.36px]' : 'bottom-[7.36px]'}`}
                        style={badge.style}
                    >
                        {badge.label}
                    </div>
                )}
            </div>

            {/* Product Info */}
            <div className="product-card__info flex flex-col p-2 gap-0 mt-1 w-full relative mt-auto">
                {/* Vendor */}
                {settings.show_vendor && safeProduct.brand && (
                    <span className="text-xs uppercase font-gerstner color-text">{safeProduct.brand}</span>
                )}

                {/* Title */}
                <h2 className="product-card__title font-gerstner text-sm font-medium uppercase tracking-[0.42px]">
                    <a href={safeProduct.url} className="color-text hover:color-primary-2">
                        {safeProduct.name}
                    </a>
                </h2>

                {/* Price and Savings */}
                <div
                    className="flex items-center justify-between mt-1"
                    role="group"
                    aria-label="Price"
                >
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 flex-wrap overflow-hidden product-price">
                            {isOnSale ? (
                                <>
                                    {settings.price_position === "compare_first" ? (
                                        <>
                                            <span
                                                className="text-sm line-through font-gerstner shrink-0 leading-[150%]"
                                                style={{ color: settings.compare_at_color }}
                                            >
                                                {formatPrice(displayCompareAtPrice)}
                                            </span>
                                            <span className="text-sm font-gerstner font-normal color-text leading-[150%] tabular-nums shrink-0">
                                                {formatPrice(productPrice)}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="text-sm font-gerstner font-normal color-text leading-[150%] tabular-nums shrink-0">
                                                {formatPrice(productPrice)}
                                            </span>
                                            <span
                                                className="text-sm line-through font-gerstner shrink-0 leading-[150%]"
                                                style={{ color: settings.compare_at_color }}
                                            >
                                                {formatPrice(displayCompareAtPrice)}
                                            </span>
                                        </>
                                    )}
                                    <div className="max-lg:w-full">
                                        <span
                                            className="flex-shrink-0 inline-flex justify-center items-center px-2 py-0 gap-1 text-xs font-gerstner font-medium border border-solid leading-[150%] tabular-nums whitespace-nowrap tracking-[0.24px] uppercase"
                                            style={{
                                                color: settings.badge_savings_text,
                                                borderColor: settings.badge_savings_border,
                                                borderRadius: `${settings.badge_corner_radius}px`,
                                            }}
                                        >
                                            Save up to {savingsPercentage}%
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <span className="text-sm font-gerstner font-normal color-text leading-[150%] tabular-nums">
                                    {formatPrice(productPrice)}
                                </span>
                            )}
                        </div>

                        {/* Rating */}
                        {settings.show_rating && rating && (
                            <div className="product-card__rating flex items-center mt-2">
                                <div className="stars flex mr-2">
                                    {[...Array(ratingScaleMax)].map((_, i) => (
                                        <span
                                            key={i}
                                            className={i < Math.round(rating) ? "color-primary-2 text-xs" : "text-gray-300 text-xs"}
                                        >
                                            ★
                                        </span>
                                    ))}
                                </div>
                                <span className="text-xs color-text">({ratingCount})</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Swatches and Buttons */}
                <div className={`product-card__swatch-and-buttons ${context === 'search' ? 'flex items-end self-stretch py-[2px] lg:py-2' : 'flex items-center justify-between mt-auto pt-2 md:pt-0 md:mt-1 gap-2'}`}>
                    {/* Color Swatches */}
                    {settings.show_color_swatches &&
                        optionTypes.find(opt => opt.key === "colour")?.values.length > 0 && (
                            <div className="product-card__swatches flex flex-col items-end gap-2 relative">
                                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Color options">
                                    {optionTypes
                                        .find(opt => opt.key === "colour")
                                        .values.slice(0, 50)
                                        .map((colorHandle, index) => {
                                            const activeFilterColor = document.querySelector('#collectionTemplate') ? document.querySelector('#collectionTemplate').getAttribute('data-filter-selected') : '';
                                            const isSelected = ((activeFilterColor ? activeFilterColor : selectedOptions["colour"]) === colorHandle);

                                            const isColorAvailable = safeProduct.skus?.some(sku => {
                                                const colorField = sku.customFields.find(field => field.key === "colour");
                                                const skuColorHandle = colorField ? handleize(colorField.value) : null;
                                                return skuColorHandle === colorHandle && sku.availability === "InStock";
                                            });
                                            // const swatchImage = settings.color_swatch_images?.[colorHandle] || "";

                                            const swatchImages = window?.plpSettings?.color_swatch_images;
                                            const firstKey = swatchImages ? Object.keys(swatchImages)[0] : null;
                                            const firstValue = firstKey ? swatchImages?.[firstKey] : null;
                                            const themeId = firstValue?.split('/t/')[1]?.split('/asset')[0] || '541';

                                            const swatchImage = `https://www.vitalydesign.com/cdn/shop/t/${themeId}/assets/${colorHandle}.png`;

                                            return (
                                                <div 
                                                    key={colorHandle} 
                                                    className={`relative inline-flex items-center ${index > 2 ? 'max-md:hidden' : ''} ${index > 3 ? 'hidden' : ''}`}
                                                >
                                                    <input
                                                        type="radio"
                                                        id={`swatch-${safeProduct.productId}-${colorHandle}`}
                                                        name={`color-${safeProduct.productId}`}
                                                        value={colorHandle}
                                                        className="sr-only peer variant-option color-option"
                                                        checked={isSelected}
                                                        onChange={() => handleOptionChange("colour", colorHandle)}
                                                        aria-label={colorHandle.replace(/-/g, " ")}
                                                    />
                                                    <label
                                                        htmlFor={`swatch-${safeProduct.productId}-${colorHandle}`}
                                                        className={`color-swatch block w-5 h-5 rounded-full overflow-visible border border-gray-500 relative cursor-pointer hover:border-gray-900 peer-checked:border-1 peer-checked:border-gray-900 transition-all duration-200 ${isSelected ? 'active' : ''}`}
                                                        style={{
                                                            width: `${settings.color_swatch_size}px`,
                                                            height: `${settings.color_swatch_size}px`,
                                                        }}
                                                        data-color={colorHandle}
                                                        role="radio"
                                                        aria-checked={isSelected}
                                                        onClick={() => handleOptionChange("colour", colorHandle)}
                                                    >
                                                        <span
                                                            id={colorHandle}
                                                            className="swatch-inner block w-full h-full rounded-full overflow-hidden"
                                                            style={{
                                                                backgroundColor: swatchImage ? "transparent" : "#CCCCCC",
                                                            }}
                                                        >
                                                            {swatchImage && (
                                                                <img
                                                                    src={swatchImage}
                                                                    alt={colorHandle.replace(/-/g, " ")}
                                                                    className="w-full h-full object-cover rounded-full transition-transform hover:scale-90 peer-checked:scale-90"
                                                                    width={settings.color_swatch_size}
                                                                    height={settings.color_swatch_size}
                                                                    loading="lazy"
                                                                />
                                                            )}
                                                        </span>
                                                        {!isColorAvailable && (
                                                            <div className="absolute inset-0">
                                                                <span className="absolute inset-0 bg-black/30 rounded-full z-2"></span>
                                                                <span className="absolute inset-0 z-2 pointer-events-none">
                                                                    <svg
                                                                        width="100%"
                                                                        height="100%"
                                                                        viewBox="0 0 28 28"
                                                                        fill="none"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                    >
                                                                        <path
                                                                            d="M24 4L4 24"
                                                                            stroke="white"
                                                                            stroke-width="1.333"
                                                                            stroke-linecap="round"
                                                                            stroke-linejoin="round"
                                                                        />
                                                                    </svg>
                                                                </span>
                                                            </div>
                                                        )}
                                                    </label>
                                                    {index === 2 &&
                                                        optionTypes.find(opt => opt.key === "colour").values.length > 3 && (
                                                            <span className="text-xs color-text ml-2 md:hidden mt-[2px]">
                                                                +{optionTypes.find(opt => opt.key === "colour").values.length - 3}
                                                            </span>
                                                        )}
                                                    {index === 3 &&
                                                        optionTypes.find(opt => opt.key === "colour").values.length > 5 && (
                                                            <span className="text-xs color-text ml-1 hidden md:block">
                                                                +{optionTypes.find(opt => opt.key === "colour").values.length - 5}
                                                            </span>
                                                        )}
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        )}

                    {/* Wishlist and Quick Add Buttons */}
                    <div
                        className={`flex items-center gap-[2px] ${
                            settings.show_color_swatches && optionTypes.find(opt => opt.key === "colour")?.values.length > 0
                                ? "ml-auto"
                                : ""
                        }`}
                    >
                        {settings.show_wishlist && (
                            // <button
                            //     className={`wishlist-button product_${selectedVariant.id || safeProduct.skus?.[0]?.id} flex justify-center items-center w-8 h-8 p-[9.412px] gap-[9.412px] border border-solid color-border-dark transition-all duration-200 ${
                            //         settings.square_buttons ? "" : "rounded-full"
                            //     }`}
                            //     aria-label="Add to wishlist"
                            //     data-with-epi="true"
                            //     data-product-id={safeProduct.productId}
                            //     data-swaction="addToWishlist"
                            //     data-product-id={safeProduct.productId}
                            //     data-variant-id={selectedVariant.id || safeProduct.skus?.[0]?.id || ""}
                            //     data-product-url={`${window.Shopify?.routes?.root.slice( 1 ) || ""}${safeProduct.url}`}
                            // >
                            //     <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            //         <g clipPath="url(#clip0_1973_2811)">
                            //             <path d="M13.1307 5.0778C13.1307 6.02579 12.7667 6.9363 12.1167 7.60983C10.6205 9.16072 9.16927 10.778 7.61715 12.2726C7.26135 12.6103 6.69698 12.598 6.35655 12.2451L1.88481 7.60983C0.533187 6.20875 0.533187 3.94686 1.88481 2.54579C3.24973 1.13096 5.47331 1.13096 6.83821 2.54579L7.00076 2.71427L7.16323 2.54589C7.81764 1.86718 8.70893 1.48438 9.63997 1.48438C10.571 1.48438 11.4623 1.86714 12.1167 2.54579C12.7668 3.21939 13.1307 4.12986 13.1307 5.0778Z" stroke="#191919" strokeWidth="0.960784" strokeLinejoin="round"/>
                            //         </g>
                            //         <defs>
                            //             <clipPath id="clip0_1973_2811">
                            //                 <rect width="13.1765" height="13.1765" fill="white" transform="translate(0.412109 0.412109)"/>
                            //             </clipPath>
                            //         </defs>
                            //     </svg>
                            // </button>
                            <swymcs-wishlist-button className="swymcs-wishlist-button">
                                <button
                                    id="swymcs-wishlist-button"
                                    className={`swymcs-wishlist-button swymcs-wishlist-button-${safeProduct.productId} flex justify-center items-center w-8 h-8 p-[9.412px] gap-[9.412px] border border-solid color-border-dark transition-all duration-200 ${settings.square_buttons ? "" : "rounded-full"}`}
                                    data-empi={safeProduct.productId}
                                    data-epi={selectedVariant.id || safeProduct.skus?.[0]?.id || ""}
                                    data-du={`${window.Shopify?.routes?.root.slice( 1 ) || ""}${safeProduct.url}`}
                                    aria-label="Add to wishlist"
                                >
                                    <div id="swymUnfilled">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <g clipPath="url(#clip0_1973_2811)">
                                                <path d="M13.1307 5.0778C13.1307 6.02579 12.7667 6.9363 12.1167 7.60983C10.6205 9.16072 9.16927 10.778 7.61715 12.2726C7.26135 12.6103 6.69698 12.598 6.35655 12.2451L1.88481 7.60983C0.533187 6.20875 0.533187 3.94686 1.88481 2.54579C3.24973 1.13096 5.47331 1.13096 6.83821 2.54579L7.00076 2.71427L7.16323 2.54589C7.81764 1.86718 8.70893 1.48438 9.63997 1.48438C10.571 1.48438 11.4623 1.86714 12.1167 2.54579C12.7668 3.21939 13.1307 4.12986 13.1307 5.0778Z" stroke="#191919" strokeWidth="0.960784" strokeLinejoin="round"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_1973_2811">
                                                    <rect width="13.1765" height="13.1765" fill="white" transform="translate(0.412109 0.412109)"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </div>
                                    <div id="swymFilled" style="display:none">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <g clipPath="url(#clip0_1973_2811)">
                                                <path d="M13.1307 5.0778C13.1307 6.02579 12.7667 6.9363 12.1167 7.60983C10.6205 9.16072 9.16927 10.778 7.61715 12.2726C7.26135 12.6103 6.69698 12.598 6.35655 12.2451L1.88481 7.60983C0.533187 6.20875 0.533187 3.94686 1.88481 2.54579C3.24973 1.13096 5.47331 1.13096 6.83821 2.54579L7.00076 2.71427L7.16323 2.54589C7.81764 1.86718 8.70893 1.48438 9.63997 1.48438C10.571 1.48438 11.4623 1.86714 12.1167 2.54579C12.7668 3.21939 13.1307 4.12986 13.1307 5.0778Z" stroke="#191919" fill="#191919" strokeWidth="0.960784" strokeLinejoin="round"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_1973_2811">
                                                    <rect width="13.1765" height="13.1765" fill="white" transform="translate(0.412109 0.412109)"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </div>
                                </button>
                            </swymcs-wishlist-button>
                      
                        )}
                        {(settings.show_quick_add && context !== 'search') && (
                            <button
                                className={`quick-add-btn flex justify-center items-center w-8 h-8 p-[9.412px] gap-[9.412px] border border-solid color-border-dark transition-all duration-200 ${settings.square_buttons ? "" : "rounded-full"} ${context === 'search' ? 'hidden' : ''}`}
                                aria-label="Quick add"
                                aria-haspopup="dialog"
                                aria-controls={`quickAdd-${safeProduct.productId}`}
                                data-product-id={safeProduct.productId}
                                data-variant-id={selectedVariant.id || safeProduct.skus?.[0]?.id || ""}
                                data-quickadd-toggle={safeProduct.skus.length > 1 ? '' : null}
                                data-direct-add={safeProduct.skus.length < 2 ? '' : null}
                            >
                                {settings.quick_add_icon && !settings.quick_add_icon.includes("no-image") ? (
                                    <img src={settings.quick_add_icon} alt="Quick add" width="16" height="16" />
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <g clipPath="url(#clip0_1973_2814)">
                                            <path d="M7.00035 0.412109V7.00035M7.00035 7.00035V13.5886M7.00035 7.00035H13.5886M7.00035 7.00035L0.412109 7.00035" stroke="#191919" strokeWidth="0.686275" strokeLinecap="round" strokeLinejoin="round"/>
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_1973_2814">
                                                <rect width="13.1765" height="13.1765" fill="white" transform="translate(0.412109 0.412109)"/>
                                            </clipPath>
                                        </defs>
                                    </svg>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Quick Add Popover */}
            {settings.show_quick_add && (
                <div
                    id={`quickAdd-${safeProduct.productId}`}
                    className="quick-add-popover flex flex-col justify-center items-center gap-4 w-full border-t border-solid color-border-dark bg-white p-4 absolute bottom-[-1px] left-[-1px] z-10 hidden"
                    style={{
                        width: 'calc(100% + 1px)',
                        opacity: settings.quick_add_modal_opacity / 100,
                        borderRadius: `${settings.quick_add_modal_radius}px ${settings.quick_add_modal_radius}px 0 0`,
                    }}
                    aria-label="Quick add options"
                >
                    <form
                        className="quick-add-form w-full"
                        data-product-id={safeProduct.productId}
                        data-product-handle={safeProduct.pid}
                        onSubmit={e => {
                            e.preventDefault();
                            let formData = {
                                items: [
                                    {
                                        id: e.target.querySelector("[data-variant-id]").value,
                                        quantity: e.target.querySelector("quantity-input input")?.value || 1,
                                    },
                                ],
                            };
                            fetch(window.Shopify.routes.root + "cart/add.js", {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                },
                                body: JSON.stringify(formData),
                            })
                                .then(response => {
                                    let sidecart = document.querySelector("side-cart");
                                    sidecart?.openCart();
                                })
                                .catch(error => {
                                    console.error("Error:", error);
                                });
                        }}
                    >
                        {/* Render all variant options */}
                        {optionTypes.map((optionType, optionIndex) => {
                            // Get the selected color (if applicable)
                            const selectedColor = selectedOptions["colour"] || selectedOptions["color"];

                            // Filter SKUs based on the selected color (if not rendering color option)
                            const filteredSkus = useMemo(() => {
                                return optionType.key === "colour" || optionType.key === "color"
                                    ? safeProduct.skus
                                    : safeProduct.skus.filter(sku => {
                                          const colorField = sku.customFields.find(field => field.key === "colour" || field.key === "color");
                                          return colorField && handleize(colorField.value) === selectedColor;
                                      });
                            }, [selectedColor, safeProduct.skus, optionType.key]);

                            // Get available values for this option type based on filtered SKUs
                            const availableValues = new Set();
                            filteredSkus.forEach(sku => {
                                const field = sku.customFields.find(f => f.key === optionType.key);
                                if (field && sku.availability === "InStock") {
                                    availableValues.add(handleize(field.value));
                                }
                            });

                            return (
                                <div key={optionType.key} className={`product-option mb-3 w-full ${optionType.name.toLowerCase()}`}>
                                    <h4 className="option-name text-sm font-medium mb-2">
                                        {optionType.name}: <span className={`selected-option-value ${optionType.name.toLowerCase()} text-xs font-normal text-gray-500`}>{optionType.name == 'Colour' ? selectedVariant.name.substr(0, selectedVariant.name.lastIndexOf("/")).replace(/\//g, " ").toLowerCase() : " " } {optionType.name == 'Size' ? selectedVariant.name.split("/").pop().toLowerCase() : " " }</span>
                                    </h4>
                                    <div className="option-values flex flex-col items-start gap-2 self-stretch">
                                        <div className="flex flex-wrap gap-2" role="radiogroup">
                                            {optionType.values.map(value => {
                                                const activeFilterColor = document.querySelector('#collectionTemplate').getAttribute('data-filter-selected');
                                                const isSelected = selectedOptions[optionType.key] === value;
                                                
                                                const isValueAvailable = availableValues.has(value);
                                                const swatchImage =
                                                    optionType.key === "colour" ? settings.color_swatch_images?.[value] || "" : "";

                                                    // Find matching variant to get inventory
                                                const matchingVariant = filteredSkus.find(sku => {
                                                    const field = sku.customFields.find(f => f.key === optionType.key);
                                                    return field && handleize(field.value) === value;
                                                });
                                                return (
                                                    <div key={value} className="relative">
                                                        <input
                                                            type="radio"
                                                            id={`option-${safeProduct.productId}-${optionType.key}-${value}`}
                                                            name={`option-${optionIndex + 1}`}
                                                            value={value}
                                                            className="peer sr-only variant-option"
                                                            checked={isSelected}
                                                            onChange={() => handleOptionChange(optionType.key, value)}
                                                            data-option-position={optionIndex + 1}
                                                            data-option-value={value.replaceAll('-', ' ')}
                                                        />
                                                        <label
                                                            htmlFor={`option-${safeProduct.productId}-${optionType.key}-${value}`}
                                                            className={`relative option-button cursor-pointer inline-flex justify-center items-center border border-transparent text-xs font-medium bg-white hover:border-[#191919] peer-checked:border-[#191919] transition-all duration-200 ${
                                                                optionType.key === "colour"
                                                                    ? "w-5 h-5 rounded-full overflow-visible border border-gray-500"
                                                                    : "text-sm w-[68px] h-[31px] py-3"
                                                            } ${isValueAvailable ? "" : "soldout"}`}
                                                            title={value.replace(/-/g, " ")}
                                                            role="radio"
                                                            aria-checked={isSelected}
                                                            onClick={() => isValueAvailable && handleOptionChange(optionType.key, value)}
                                                            data-inventory={matchingVariant?.inventoryLevel}
                                                        >
                                                            {optionType.key === "colour" ? (
                                                                <span
                                                                    className="swatch-inner block w-full h-full rounded-full overflow-hidden"
                                                                    style={{
                                                                        backgroundColor: swatchImage ? "transparent" : "#CCCCCC",
                                                                    }}
                                                                >
                                                                    {swatchImage && (
                                                                        <img
                                                                            src={swatchImage}
                                                                            alt={value.replace(/-/g, " ")}
                                                                            className="w-full h-full object-cover rounded-full transition-transform hover:scale-90 peer-checked:scale-90"
                                                                            width="20"
                                                                            height="20"
                                                                            loading="lazy"
                                                                        />
                                                                    )}
                                                                </span>
                                                            ) : (
                                                                <span>{value.replace(/-/g, " ")}</span>
                                                            )}
                                                            {!isValueAvailable && (
                                                                <div className="absolute inset-0">
                                                                        {optionType.key === 'colour' ? (
                                                                        <span className="absolute inset-0 bg-black/30 rounded-full z-2"></span>
                                                                        ) : null}                                                                   
                                                                        <span className={`absolute inset-0 z-2 pointer-events-none ${optionType.key === 'colour' ? '' : 'opacity-70 bg-white'}`}>
                                                                        <svg
                                                                            width="100%"
                                                                            height="100%"
                                                                            viewBox="0 0 28 28"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                d="M24 4L4 24"
                                                                                stroke="white"
                                                                                stroke-width="1.333"
                                                                                stroke-linecap="round"
                                                                                stroke-linejoin="round"
                                                                            />
                                                                        </svg>
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {optionType.key === 'size' && (
                                                                <span 
                                                                class={`notification-icon ml-1 w-3 h-[13px] mb-1 ${isValueAvailable ? "hidden" : "block"}`}
                                                                data-option-type={optionType.key}
                                                                >
                                                                    <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="12"
                                                                    height="13"
                                                                    viewBox="0 0 12 13"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    >
                                                                    <g clip-path="url(#clip0_647_897)">
                                                                        <path d="M4.24992 9.81071V10.394C4.24992 10.8582 4.43429 11.3033 4.76248 11.6315C5.09067 11.9597 5.53579 12.144 5.99992 12.144C6.46405 12.144 6.90917 11.9597 7.23736 11.6315C7.56554 11.3033 7.74992 10.8582 7.74992 10.394V9.81071M4.83325 2.81071C4.83325 2.50129 4.95617 2.20454 5.17496 1.98575C5.39375 1.76696 5.6905 1.64404 5.99992 1.64404C6.30934 1.64404 6.60608 1.76696 6.82488 1.98575C7.04367 2.20454 7.16659 2.50129 7.16659 2.81071C7.83649 3.12748 8.40757 3.62057 8.81862 4.23713C9.22966 4.8537 9.46518 5.5705 9.49992 6.31071V8.06071C9.54382 8.42337 9.67226 8.77066 9.87489 9.07462C10.0775 9.37857 10.3487 9.6307 10.6666 9.81071H1.33325C1.65113 9.6307 1.92231 9.37857 2.12494 9.07462C2.32758 8.77066 2.45602 8.42337 2.49992 8.06071V6.31071C2.53466 5.5705 2.77018 4.8537 3.18122 4.23713C3.59227 3.62057 4.16335 3.12748 4.83325 2.81071Z" stroke="#191919" stroke-width="0.857143" stroke-linecap="round" stroke-linejoin="round"/>
                                                                    </g>
                                                                    <defs>
                                                                        <clipPath id="clip0_647_897">
                                                                        <rect width="12" height="12" fill="white" transform="translate(0 0.894043)"/>
                                                                        </clipPath>
                                                                    </defs>
                                                                    </svg>
                                                                </span>
                                                            )}
                                                            
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <input type="hidden" name="id" value={selectedVariant.id} data-variant-id />
                        <input
                            type="hidden"
                            name="variant_available"
                            value={selectedVariant.availability === "InStock"}
                            data-variant-available
                        />

                        <div className="flex-1">
                            <div className={`${settings.show_quantity_selector ? 'flex items-center space-x-2' : ''}`}>
                                {settings.show_quantity_selector && (
                                    <quantity-input
                                        className="
                                            flex-grow-0 flex-shrink-0 h-[42px] lg:h-[45px] w-[80px]
                                            flex
                                            py-1
                                            px-2
                                            justify-between
                                            items-center
                                            border
                                            border-solid
                                            color-border-dark
                                            color-bg-1
                                            color-text
                                        "
                                    >
                                        <button
                                            className="quantity__button border-0 w-[10px] h-[10px] flex items-center justify-center stroke-current color-text hover:bg-[#66666640] transition-colors duration-200"
                                            name="minus"
                                            type="button"
                                            onClick={quantityClick}
                                        >
                                            <span className="hidden">
                                            Decrease quantity
                                            </span>
                                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <g id="minus-sm">
                                                <path id="Icon" d="M18 12.7372H6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                                </g>
                                            </svg>
                                        </button>
                                        <input
                                            className="quantity__input border-0 p-0 max-w-[40px] lg:max-w-[50%] w-min flex py-0 px-2 text-center items-center content-center gap-[2px] font-gerstner text-xs leading-[150%] bg-white color-bg-1"
                                            type="number"
                                            name="updates[]"
                                            style="padding: 0;"
                                            value="1"
                                            min="0"
                                            aria-label="Quantity"
                                            id={`Quantity-${Number(productIndex) + 1}`}
                                            data-index={Number(productIndex) + 1}
                                        />
                                        <button
                                            class="quantity__button w-[10px] h-[10px] no-js-hidden border-0 flex items-center justify-center stroke-current color-text hover:bg-[#66666640] transition-colors duration-200"
                                            name="plus"
                                            type="button"
                                            onClick={quantityClick}
                                        >
                                            <span class="hidden">
                                            Increase quantity
                                            </span>
                                            <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path id="Icon" d="M12.5 7.05981V13.3098M12.5 13.3098V19.5598M12.5 13.3098H18.75M12.5 13.3098L6.25 13.3098" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                            </svg>
                                        </button>
                                    </quantity-input>
                                )}
                                <button
                                    type="submit"
                                    className="w-full btn-primary-dark"
                                    data-add-to-cart
                                    style={{ display: selectedVariant.availability === "InStock" ? "block" : "none" }}
                                >
                                    <span>{settings.translations?.add_to_cart || "Add to Cart"}</span>
                                </button>
                                <button
                                    type="button"
                                    className="w-full py-2 h-[42px] lg:h-[45px] border border-solid border-[#191919] text-sm font-medium bg-transparent hover:bg-gray-50 transition-colors duration-200"
                                    data-notify-me
                                    style={{ display: selectedVariant.availability === "InStock" ? "none" : "block" }}
                                    data-product-id={safeProduct.productId}
                                    data-variant-id={selectedVariant.id}
                                >
                                    {settings.translations?.unavailable || "Notify Me"}
                                </button>
                            </div>
                        </div>
                    </form>
                    <button
                        className="absolute top-2 right-2 text-gray-500 hover:text-black"
                        data-quickadd-close
                        aria-label="Close quick add"
                    >
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M12 4L4 12"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                            <path
                                d="M4 4L12 12"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>
            )}
            </div>
        </SerpElement>
    );
}